const key = 'refimiro-recently-viewed-v1';

export function normalizeHistory(value) {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter(handle => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(handle)))].slice(0, 9);
}

export function rememberProduct(history, handle) {
  return normalizeHistory([handle, ...normalizeHistory(history)]);
}

function readHistory() {
  try { return normalizeHistory(JSON.parse(localStorage.getItem(key))); }
  catch { return []; }
}

async function loadCard(handle) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);
  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=rm-recently-viewed`, { signal: controller.signal });
    if (!response.ok) return null;
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const card = doc.querySelector('.rm-recent__card');
    return card?.dataset.recentHandle === handle ? card : null;
  } catch { return null; }
  finally { clearTimeout(timeout); }
}

function mount(root) {
  const track = root.querySelector('[data-recent-track]');
  const prev = root.querySelector('[data-recent-prev]');
  const next = root.querySelector('[data-recent-next]');
  const fallback = root.parentElement.querySelector('[data-recent-fallback]');
  let generation = 0;
  let started = false;
  let drag = null;
  const sync = () => {
    prev.disabled = track.scrollLeft < 2;
    next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
  };
  async function refresh() {
    const run = ++generation;
    const handles = readHistory().filter(handle => handle !== root.dataset.recentExclude);
    const cards = (await Promise.all(handles.map(loadCard))).filter(Boolean);
    if (run !== generation) return;
    track.replaceChildren(...cards);
    root.hidden = cards.length === 0;
    if (fallback) fallback.hidden = cards.length > 0;
    sync();
  }
  const move = direction => track.scrollBy({ left: direction * track.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  prev.addEventListener('click', () => move(-1));
  next.addEventListener('click', () => move(1));
  track.addEventListener('scroll', sync, { passive: true });
  new ResizeObserver(sync).observe(track);
  track.addEventListener('keydown', event => {
    if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    move(event.key === 'ArrowRight' ? 1 : -1);
  });
  track.addEventListener('dragstart', event => event.preventDefault());
  track.addEventListener('pointerdown', event => {
    drag = null;
    if (event.pointerType === 'mouse' && event.button === 0) drag = { x: event.clientX, left: track.scrollLeft, moved: false };
  });
  track.addEventListener('pointermove', event => {
    if (!drag || event.buttons !== 1) return;
    const distance = event.clientX - drag.x;
    if (Math.abs(distance) > 5) drag.moved = true;
    if (!drag.moved) return;
    event.preventDefault();
    track.setPointerCapture(event.pointerId);
    track.style.scrollSnapType = 'none';
    track.style.scrollBehavior = 'auto';
    track.scrollLeft = drag.left - distance;
  });
  const release = () => {
    track.style.removeProperty('scroll-snap-type');
    track.style.removeProperty('scroll-behavior');
  };
  track.addEventListener('pointerup', release);
  track.addEventListener('pointercancel', () => { release(); drag = null; });
  track.addEventListener('click', event => {
    if (drag?.moved) { event.preventDefault(); event.stopPropagation(); drag = null; }
  });
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      observer.disconnect();
      started = true;
      refresh();
    }, { rootMargin: '500px' });
    observer.observe(root.parentElement);
  } else { started = true; refresh(); }
  window.addEventListener('pageshow', event => { if (event.persisted && started) refresh(); });
}

if (typeof document !== 'undefined') {
  const product = document.querySelector('script[data-recent-product]')?.dataset.recentProduct;
  if (product) {
    const record = () => {
      try { localStorage.setItem(key, JSON.stringify(rememberProduct(readHistory(), product))); } catch {}
    };
    record();
    window.addEventListener('pageshow', event => { if (event.persisted) record(); });
  }
  document.querySelectorAll('[data-recently-viewed]').forEach(mount);
}
